import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SettingDef, SettingsView } from '@project/contracts'
import { Button, Notice, Panel, Select } from '../design/index.js'
import { useApi } from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDate } from '../framework/format.js'

/**
 * Business settings — docs/11 §Setup → Settings.
 *
 * A long list of switches, each with the paragraph that explains it, because
 * the person throwing one of these is deciding whether a rule refuses or
 * merely warns. Every switch says what it will do and, once changed, who
 * changed it and when — the observed system shows "Inventory enabled on
 * 17-Dec-2024" and that stamp is worth keeping.
 */
export const SettingsPage = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const defs = useQuery({ queryKey: ['settingDefs'], queryFn: () => api.settingDefs(), staleTime: 10 * 60_000 })
  const view = useQuery({ queryKey: ['settings', api.companyId], queryFn: () => api.settings() })

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean | number | string }) => api.setSetting(key, value),
    onSuccess: (next: SettingsView, variables) => {
      client.setQueryData(['settings', api.companyId], next)
      // A setting can put a document type on or off the menu, so the menu and
      // anything already fetched under the old answer are no longer trusted.
      void client.invalidateQueries({ queryKey: ['menu'] })
      void client.invalidateQueries({ queryKey: ['docDefs'] })
      setError(null)
      setSaved(variables.key)
    },
    onError: (err) => {
      setError(messageFor(err))
      setSaved(null)
    },
  })

  const groups = useMemo(() => {
    const byGroup = new Map<string, SettingDef[]>()
    for (const def of defs.data ?? []) {
      const list = byGroup.get(def.group)
      if (list) list.push(def)
      else byGroup.set(def.group, [def])
    }
    return [...byGroup.entries()]
  }, [defs.data])

  if (defs.isLoading || view.isLoading) return <div className="page"><Loading /></div>
  if (defs.error) return <div className="page"><ErrorBlock error={defs.error} /></div>
  if (view.error) return <div className="page"><ErrorBlock error={view.error} /></div>

  const values = view.data?.values ?? {}
  const changed = view.data?.changed ?? {}

  return (
    <div className="page">
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">
        How this company works. Each of these changes what the system does, not how it looks.
      </p>

      {error ? <div style={{ margin: '18px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      {groups.map(([group, items]) => (
        <Panel key={group} title={group}>
          <div className="settings">
            {items.map((def) => {
              const stamp = changed[def.key]
              const busy = save.isPending && save.variables?.key === def.key
              return (
                <div className="setting" key={def.key}>
                  <div className="setting__body">
                    <div className="setting__label">{def.label}</div>
                    <p className="setting__blurb">{def.blurb}</p>
                    <p className="setting__meta">
                      {def.effect}
                      {stamp ? ` · Set ${formatDate(stamp.at.slice(0, 10))}${stamp.by ? ` by ${stamp.by}` : ''}` : ''}
                      {def.irreversible && stamp ? ' · This cannot be changed again.' : ''}
                    </p>
                    {saved === def.key && !save.isPending ? (
                      <p className="setting__saved">Saved.</p>
                    ) : null}
                  </div>

                  <div className="setting__control">
                    {def.kind === 'boolean' ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={values[def.key] === true}
                        aria-label={def.label}
                        className={values[def.key] === true ? 'switch switch--on' : 'switch'}
                        disabled={busy}
                        onClick={() => save.mutate({ key: def.key, value: values[def.key] !== true })}
                      >
                        <span className="switch__knob" />
                        <span className="switch__text">{values[def.key] === true ? 'On' : 'Off'}</span>
                      </button>
                    ) : def.kind === 'select' ? (
                      <div style={{ width: 200 }}>
                        <Select
                          value={String(values[def.key] ?? def.defaultValue)}
                          onChange={(value) => save.mutate({ key: def.key, value })}
                          options={[...(def.options ?? [])]}
                          aria-label={def.label}
                        />
                      </div>
                    ) : (
                      <TextSetting
                        def={def}
                        value={String(values[def.key] ?? '')}
                        busy={busy}
                        onSave={(value) => save.mutate({ key: def.key, value })}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Panel>
      ))}
    </div>
  )
}

/** Text and number settings save on a press, not on every keystroke. */
const TextSetting = ({
  def, value, busy, onSave,
}: {
  def: SettingDef
  value: string
  busy: boolean
  onSave: (value: string) => void
}) => {
  const [draft, setDraft] = useState(value)
  const dirty = draft.trim() !== value.trim()

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <input
        className="control"
        style={{ width: 260 }}
        value={draft}
        aria-label={def.label}
        inputMode={def.kind === 'number' ? 'numeric' : undefined}
        onChange={(e) => setDraft(e.currentTarget.value)}
      />
      <Button kind="ghost" disabled={busy || !dirty} onClick={() => onSave(draft.trim())}>
        Save
      </Button>
    </div>
  )
}
